import { IndividualGptMetricType } from '@app/interfaces/gpt-metric.type';
import {
  Button,
  CustomLabel,
  FormTabSwitcher,
  Input,
  Select,
  TextArea,
  Toggle,
  UploadButton,
} from '@ui';
import React, { FC } from 'react';
import { Filter } from '@app/components/ui/easy-filter/types';

import { Controller, useForm } from 'react-hook-form';
import {
  useCreateMetricTemplateMutation,
  useEditMetricTemplateMutation,
  useGetMetricTemplateGroupsListQuery,
} from '@app/store/api/metric-template.api';
import { MetricListArrType } from '@app/interfaces/pages-types/anatylics-metric.type';
import { MetricTemplatesType } from '@app/interfaces/slices-types/metric-templates.type';
import useTranslation from '@app/hooks/use-translation';

type FormType = Omit<IndividualGptMetricType, 'dnt_display' | 'export'> & {
  validationResponseToggle: boolean;
  metricResponseType: 'num' | 'str' | 'text';
  validationValue: string;
  select: string;
};

const emptyFilter: Filter = {
  filter: [],
};

type AiSingleMetricForTemplatesPropsType = {
  editModal?: boolean;
  copyModal?: boolean;
  modalsSetState: (type: string) => void;
  currentTemplate: 'chat' | 'voice';
  metricTemplateSettings?: MetricTemplatesType | undefined;
  metricList: MetricListArrType[];
};

const AiSingleMetricForTemplates: FC<AiSingleMetricForTemplatesPropsType> = (props) => {
  const { t: tf } = useTranslation('common.forms');
  const { t } = useTranslation('pages.metricTemplates');

  const { modalsSetState, editModal, metricTemplateSettings, copyModal, currentTemplate } = props;
  // api
  const { data: metricTemplates } = useGetMetricTemplateGroupsListQuery(currentTemplate);

  const [editMetricTemplate] = useEditMetricTemplateMutation();
  const [createMetricTemplate] = useCreateMetricTemplateMutation();
  // api

  const getSelectItems = metricTemplates?.map((e) => {
    return { title: e.name, value: e.metric_templates_group_id };
  });

  const defaultValues: FormType =
    editModal || copyModal
      ? {
          ...metricTemplateSettings,
          settings: {
            prompt: metricTemplateSettings?.settings.prompt || '',
            condition: metricTemplateSettings?.settings?.condition || emptyFilter,
            validation: metricTemplateSettings?.settings.validation || { type: 'none' },
            gpt_group: undefined,
            str_validation: metricTemplateSettings?.settings.str_validation || 'text',
          },
          description: metricTemplateSettings?.description || '',
          name: `${metricTemplateSettings?.name || ''} ${copyModal ? t('copy') : ''}`.trim(),
          result_value_type: (metricTemplateSettings?.result_value_type as 'num') || 'num',
          type: metricTemplateSettings?.type || 'gpt',
          visualization: (metricTemplateSettings?.visualization as 'native') || 'native',
          validationResponseToggle: !!metricTemplateSettings?.settings?.validation?.value?.length,
          validationValue:
            metricTemplateSettings?.settings?.validation?.type === 'none'
              ? ''
              : metricTemplateSettings?.settings.validation?.value?.join(
                  `${metricTemplateSettings?.settings.validation.type === 'array' ? ',' : '-'}`,
                ) || '',
          select: metricTemplateSettings?.metric_templates_group_id || 'default',
          // dnt_display: metricTemplateSettings?.dnt_display || false,
          metricResponseType:
            metricTemplateSettings?.result_value_type === 'num'
              ? 'num'
              : metricTemplateSettings?.settings.str_validation === 'text'
              ? 'str'
              : 'text',
        }
      : {
          type: 'gpt',
          description: '',
          name: '',
          result_value_type: 'num',
          // dnt_display: false,
          visualization: 'native',
          validationResponseToggle: false, //
          // export: { export_result: false },
          metricResponseType: 'num', //
          validationValue: '', //
          select: 'default', //
          settings: {
            condition: emptyFilter,
            gpt_group: '',
            prompt: '',
            str_validation: 'text',
            validation: { type: 'none' },
            // data.result_value_type === 'num' ? undefined : data.settings?.str_validation || 'text',
            // validation: !data.validationResponseToggle
            //   ? { type: 'none' }
            //   : validationFormatter(
            //       data.validationValue,
            //       data.settings?.str_validation,
            //       data.result_value_type,
            //     ),
          },
        };

  const {
    register,
    control,
    clearErrors,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormType>({ defaultValues });

  // 1
  function handleChangeMetricResponseType(value: 'num' | 'str' | 'text') {
    setValue(`metricResponseType`, value);
    switch (value) {
      case 'num':
        setValue(`result_value_type`, 'num');
        setValue(`settings.str_validation`, undefined);
        break;
      case 'str':
        setValue(`result_value_type`, 'str');
        setValue(`settings.str_validation`, 'text');
        break;
      case 'text':
        setValue(`result_value_type`, 'str');
        setValue(`settings.str_validation`, 'longtext');
        setValue(`settings.validation`, {
          type: 'none',
        });
        setValue(`validationResponseToggle`, false);
        setValue(`validationValue`, '');
        break;

      default:
        break;
    }
  }
  // 1

  const descriptionsByMetricType: Record<'str' | 'text' | 'num', string> = {
    num: 'Целое число без дробей',
    str: 'Максимально 256 символов (включая пробелы)',
    text: 'Максимально 4000 символов (включая пробелы)',
  };

  function onSubmit(data: FormType) {
    const finalData: IndividualGptMetricType = {
      type: 'gpt',
      description: data?.description || '',
      name: data?.name || '',
      result_value_type: data.result_value_type || 'str',
      // dnt_display: data.dnt_display || false,
      settings: {
        condition: data.settings?.condition || emptyFilter,
        gpt_group: undefined,
        prompt: data.settings?.prompt || '',
        str_validation:
          data.result_value_type === 'num' ? undefined : data.settings?.str_validation || 'text',
        validation: !data.validationResponseToggle
          ? { type: 'none' }
          : validationFormatter(
              data.validationValue,
              data.settings?.str_validation,
              data.result_value_type,
            ),
      },
    };
    try {
      if (copyModal) {
        createMetricTemplate({
          params: { metric_templates_group_id: watch('select') },
          body: finalData,
        });
        return;
      }
      if (!editModal) {
        createMetricTemplate({
          params: { metric_templates_group_id: watch('select') },
          body: finalData,
        });
      } else {
        metricTemplateSettings?.metric_template_id &&
          editMetricTemplate({
            params: {
              metric_templates_group_id: watch('select'),
              id: metricTemplateSettings?.metric_template_id,
            },
            body: finalData,
          });
      }
    } finally {
      modalsSetState('gpt');
    }
  }

  function validationFormatter(
    value?: string,
    str_validation?: 'text' | 'longtext',
    result_value_type?: 'str' | 'num',
  ): FormType['settings']['validation'] {
    const defaultState = { type: 'none' as const };

    if (!value || !result_value_type) return defaultState;

    switch (result_value_type) {
      case 'num':
        if (/-/g.test(value)) {
          return { type: 'range', value: value.split('-').map((n) => Number(n.trim())) };
        } else if (/,/g.test(value)) {
          return { type: 'array', value: value.split(',').map((n) => Number(n.trim())) };
        } else {
          return defaultState;
        }
        break;
      case 'str':
        if (str_validation === 'longtext') {
          return defaultState;
        } else {
          if (/,/g.test(value)) {
            return { type: 'array', value: value.split(',').map((n) => n.trim()) };
          } else {
            return defaultState;
          }
        }

        break;

      default:
        return defaultState;
        break;
    }
  }
  // FILTER
  // function handleFilterLiveReportRecords(
  //   newFilter: Filter & Range & Partial<FilterQmOperatorsType>,
  // ) {
  //   setValue(`settings.condition`, { filter: newFilter.filter });
  // }
  // function getFilteringKeys() {
  //   return (metricList || [])?.map((metric) => ({
  //     id: metric.metric_id,
  //     title: metric.name,
  //     type_value: metric.result_value_type,
  //   }));
  // }
  // FILTER

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="relative">
      <div className="grid w-full grid-cols-[742px_356px] h-full gap-x-[18px] p-[0px_24px_24px_24px]">
        {/* GRID START */}
        <div className="flex flex-col gap-[18px]">
          <div>
            <Input
              {...register(`name`, {
                required: { message: tf('required'), value: true },
              })}
              placeholder="Введите текст"
              label="Название метрики"
              error={errors?.name?.message}
            />
          </div>
          <div className="flex flex-col gap-[6px]">
            <CustomLabel label={t('metric_templates_group')} />
            <div className="">
              <Controller
                render={() => (
                  <Select
                    error={errors.select?.message}
                    defaultValue={watch('select')}
                    options={[
                      ...(getSelectItems || []),
                      {
                        title: t('choose') + '...',
                        value: 'default',
                        disabled: true,
                        nullable: true,
                      },
                    ]}
                    onChange={(value) => {
                      setValue('select', value as string);
                      clearErrors('select');
                    }}
                  />
                )}
                name={'select'}
                rules={{ validate: (value) => value !== 'default' || tf('required') }}
                control={control}
              />
            </div>
          </div>
          <Controller
            control={control}
            name={`settings.prompt`}
            render={({ field }) => (
              <div>
                <TextArea
                  defaultValue={field.value}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value);
                  }}
                  height={183}
                  popupModal
                  placeholder="Введите ваш промпт"
                  label="Индивидуальный промпт"
                  error={errors?.settings?.prompt?.message}
                />
              </div>
            )}
            rules={{ required: { message: tf('required'), value: true } }}
          />

          {/* {isLast && (
          <>
            {isNew && (
              <div className="flex items-center justify-center">
                <Button
                  onClick={() => saveIndividualMetricToCollection('create', field.additionalId)}
                  label="Сохранить метрику"
                  fill="linked"
                  className="h-fit"
                />
              </div>
            )}
          </>
        )} */}
          {/* {!isNew && (
          <div className="flex items-center justify-center pb-4">
            <Button
              onClick={() => saveIndividualMetricToCollection('edit', field.additionalId)}
              label="Сохранить изменения"
              fill="linked"
              className="h-fit"
            />
          </div>
        )} */}
        </div>
        {/* GRID END */}
        {/* GRID START */}
        <div className="flex flex-col gap-[18px]">
          <div className="flex flex-col gap-[6px]">
            <CustomLabel label="Формат ответа" />
            <div className="flex flex-col gap-[8px]">
              <FormTabSwitcher
                valuesList={[
                  { title: 'Число', value: 'num' },
                  { title: 'Строка', value: 'str' },
                  { title: 'Текст', value: 'text' },
                ]}
                isActive={watch(`metricResponseType`) || ''}
                onChange={(value) => handleChangeMetricResponseType(value as 'num')}
              />
              <span className="text-[13px] font-normal leading-[109%] tracking-[-0.03em] text-3color opacity-50">
                {watch(`settings.str_validation`) === 'longtext'
                  ? descriptionsByMetricType['text']
                  : descriptionsByMetricType[watch(`result_value_type`) as string]}
              </span>
              <div>
                <Toggle
                  checked={watch(`validationResponseToggle`)}
                  disabled={watch(`metricResponseType`) === 'text'}
                  onChecked={(val) => setValue(`validationResponseToggle`, val)}
                  label="Включить валидацию ответа"
                />
              </div>
              <div>
                <Input
                  {...register(`validationValue`)}
                  disabled={!watch(`validationResponseToggle`)}
                  placeholder="Введите ответ для валидации"
                />
              </div>
              {/* <div>
                <Toggle
                  checked={watch(`dnt_display`)}
                  onChecked={(val) => setValue(`dnt_display`, val)}
                  label="Не отображать в просмотре записи"
                />
              </div> */}
            </div>
          </div>
          {/* <div className="flex flex-col gap-[6px]">
            <CustomLabel label="Условия запуска" />
            <div className="">
              <EasyFilterView
                disabledChangeButton
                data={{
                  ...watch(`settings.condition`),
                  range: { parameters: [], type: 'n' },
                }}
                onChangeHandler={(filter) => handleFilterLiveReportRecords(filter)}
                keysForFiltering={getFilteringKeys() || []}
                modalMode
                modalModeStyle="second"
                withoutRange
              />
            </div>
          </div> */}
          <div className="flex flex-col gap-[6px]">
            <CustomLabel label="Привязать общий файл" />
            <div>
              <UploadButton
                description={`Максимальный размер 10 MB./nФорматы DOCX, DOC, TXT, PDF./nНе поддерживаем PNG.`}
                loadedFiles={[]}
                changeLoadedFiles={() => console.log('tt')}
                descriptionPosition="right"
                soon
              />
            </div>
          </div>
        </div>
        {/* GRID END */}
      </div>
      <div className="sticky bottom-0 z-[99] bg-white pb-[24px] ml-[24px]">
        <Button type="submit" label="Сохранить" />
        <Button onClick={() => modalsSetState('gpt')} label="Отменить" fill="linked" />
      </div>
    </form>
  );
};

export default AiSingleMetricForTemplates;
